import { sortBy } from 'lodash-es'

import { isNotNull } from '@/utils/helpers'

import SessionQ from '@/queries/SessionQ'
import WorldQ from '@/queries/WorldQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import { SQL } from '@/client/query'
import { getTutorPeerReviewUrl } from '@/client/services/url'
import { getWorldLevelTitle, isLevelAtLeastMinLevel } from '@/client/services/worlds'

import { Priority, type TodoItem } from '../types'

export function usePeerReviewTodos({
  worldSlugs: worldSlugsOrig,
}: {
  worldSlugs: string[]
}): TodoItem[] {
  const currentUser = useUser()
  const query = useQuery()

  // This is a perf hack. We don't want to show peer review todos.
  // A downstream query, specifically SessionQ.anyTutorIsHostingFutureSession, is slow.
  const worldSlugs = worldSlugsOrig.slice(0, 1)

  const tutorWorldSlugs = Object.values(currentUser.worlds)
    .filter(
      world =>
        worldSlugs.includes(world.slug) &&
        isLevelAtLeastMinLevel({ level: world.level, minLevel: 'tutor' })
    )
    .map(world => world.slug)

  const sessionsToPeerReviewByWorld = query.many`FROM(
    SELECT COUNT(*) AS session_count, ${WorldQ.slug}
        ${SessionQ.where(
          {
            include: [
              {
                association: 'World',
                where: WorldQ.hasAnySlug(tutorWorldSlugs),
              },
            ],
          },
          SQL`${SessionQ.needsAuditAndCanBeAuditedByUser(
            currentUser
          )} AND ${SessionQ.isPubliclyListedForPeerReview()}`
        )}
    GROUP BY ${WorldQ.slug}
  ) AS peer_review_needs`.map(row => ({
    worldSlug: row`slug` ?? '',
    sessionCount: row`session_count`,
  }))

  const todos = sessionsToPeerReviewByWorld.map(({ worldSlug, sessionCount }) => {
    if (sessionCount === 0) {
      return null
    }

    const levelTitle = getWorldLevelTitle({ worldSlug, level: 'tutor' })

    return {
      sessionCount,
      worldSlug,
      title: 'Peer review a session',
      subtitle: `To help other ${levelTitle.toLowerCase()}s improve`,
      tag: {
        title: `${sessionCount} unreviewed`,
      },
      priority:
        sessionCount >= 20
          ? Priority.Urgent
          : sessionCount >= 10
            ? Priority.High
            : sessionCount >= 5
              ? Priority.Medium
              : Priority.Low,
      emoji: '🤝',
      completed: false,
      link: getTutorPeerReviewUrl({
        worldSlug,
      }),
    }
  })

  return sortBy(todos.filter(isNotNull), todo => -todo.sessionCount)
}
